/**
 * Email HTML display helpers (SKY-464).
 *
 * Inbound campaign replies arrive as third-party sender HTML. Rendering that
 * requires a real sanitizer (DOMPurify) — the regex sanitizeHtml in
 * sanitization.ts is fine for our own templates but not for hostile input.
 */
import DOMPurify from 'dompurify';

/** Mirrors the backend's send-path detection (sender/instantly.py): only tags the
 * composer / real email HTML actually produce. A bare "<letter…>" match would
 * also swallow prose conventions like "<jane@acme.com>" or "<https://url>". */
const HTML_TAG =
  /<\/?(?:p|br|div|span|a|b|strong|i|em|u|s|ul|ol|li|blockquote|h[1-6]|pre|code|hr|table|thead|tbody|tr|td|th)\b[^>]*>/i;

export function containsHtml(value: string): boolean {
  return HTML_TAG.test(value);
}

const EMAIL_ALLOWED_TAGS = [
  'p', 'br', 'div', 'span', 'a', 'b', 'strong', 'i', 'em', 'u', 's',
  'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'pre', 'code', 'hr',
];
// No img: inbound tracking pixels must not fire when the portal renders a reply.
// No style: DOMPurify passes CSS declarations through verbatim, so a style attr
// re-enables remote fetches (background-image:url) and viewport escapes
// (position:fixed). Formatting fidelity comes from the tag allowlist alone.
const EMAIL_ALLOWED_ATTRS = ['href', 'target', 'rel'];

// Dedicated instance: hooks on the default DOMPurify export would mutate link
// semantics for every other sanitize caller in the app.
const purifier = DOMPurify(window);

// Links from sender HTML must never navigate the portal tab or reach window.opener.
purifier.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/** Quoted-history / signature containers the major clients wrap their tails in.
 * blockquote is included deliberately: in reply display it is almost always
 * quoted history ("On … wrote:"), and our own composer can't produce one. */
const QUOTED_TAIL_SELECTORS = 'blockquote, .gmail_quote, .gmail_signature, #divRplyFwdMsg';

/**
 * Sanitize OUTBOUND campaign copy for display — the sequence steps we ingest
 * back from the sending vendor, and operator-uploaded copy variants.
 *
 * Same allowlist as inbound (it round-trips through a third party, so it gets
 * the same treatment), but NO quoted-tail trim: in outbound copy a blockquote
 * is authored content, not somebody's reply history, and cutting it would hide
 * text the campaign actually sends.
 */
export function sanitizeCampaignHtml(html: string): string {
  return purifier
    .sanitize(html, { ALLOWED_TAGS: EMAIL_ALLOWED_TAGS, ALLOWED_ATTR: EMAIL_ALLOWED_ATTRS })
    .trim();
}

/**
 * Sanitize third-party email HTML for portal display: DOMPurify allowlist,
 * then drop quoted-history/signature tails (display-only — the stored body
 * stays raw, same contract as trimQuotedReply for the text path).
 */
export function sanitizeEmailHtml(html: string): string {
  const clean = purifier.sanitize(html, {
    ALLOWED_TAGS: EMAIL_ALLOWED_TAGS,
    ALLOWED_ATTR: EMAIL_ALLOWED_ATTRS,
  });
  const doc = new DOMParser().parseFromString(clean, 'text/html');
  doc.body.querySelectorAll(QUOTED_TAIL_SELECTORS).forEach((node) => node.remove());
  const trimmed = doc.body.innerHTML.trim();
  // Same contract as trimQuotedReply: a cut that leaves no visible text means the
  // whole message was inside a "quoted" wrapper — show it untrimmed instead.
  return doc.body.textContent?.trim() ? trimmed : clean.trim();
}
